import { useMemo } from "react";
import { endOfDay, parseISO, startOfDay } from "date-fns";
import { useRecords } from "../../../../api/useRecords";
import { NFB_SERVICE } from "../../../../utils/constants";
import { NfbTherapistSchedule, TimeOffInterval } from "./types.nfbAssign";

const scheduleFieldsQuery = [
    "id",
    "start_date",
    "end_date",
    "user.id",
    "user.first_name",
    "user.last_name",
    "user.avatar",
].join(",");

const timeOffFieldsQuery = ["id", "start_date", "end_date", "user.id"].join(",");

const buildDayOverlapClauses = (clinics: Array<number>, now: Date) => {
    const clauses = [
        { start_date: { _lte: endOfDay(now).toISOString() } },
        { end_date: { _gte: startOfDay(now).toISOString() } },
        { clinic_id: { id: { _in: clinics } } },
    ];
    return clauses;
};

export const useNfbTherapistsOnSchedule = (clinics: Array<number>, now: Date) => {
    const dayOverlapClauses = buildDayOverlapClauses(clinics, now);
    const scheduleFilter = {
        _and: [...dayOverlapClauses, { services: { services_id: { id: { _eq: NFB_SERVICE } } } }],
    };
    const timeOffFilter = { _and: dayOverlapClauses };

    const { data: schedulesData } = useRecords("schedules", scheduleFieldsQuery, {
        extraOptions: { filter: scheduleFilter, pageSize: -1 },
        enabled: clinics.length > 0,
    });

    const { data: timeOffData } = useRecords("time_off", timeOffFieldsQuery, {
        extraOptions: { filter: timeOffFilter, pageSize: -1 },
        enabled: clinics.length > 0,
    });

    const therapistSchedules = useMemo(() => {
        const items = (schedulesData?.items ?? []) as Array<any>;
        const schedules: Array<NfbTherapistSchedule> = items
            .filter((item) => item?.user?.id)
            .map((item) => ({
                userId: item.user.id,
                firstName: item.user.first_name,
                lastName: item.user.last_name,
                avatar: item.user.avatar,
                start: parseISO(item.start_date),
                end: parseISO(item.end_date),
            }));
        return schedules;
    }, [schedulesData]);

    const timeOffIntervals = useMemo(() => {
        const items = (timeOffData?.items ?? []) as Array<any>;
        const intervals: Array<TimeOffInterval> = items
            .filter((item) => item?.user?.id)
            .map((item) => ({
                userId: item.user.id,
                start: parseISO(item.start_date),
                end: parseISO(item.end_date),
            }));
        return intervals;
    }, [timeOffData]);

    return { therapistSchedules, timeOffIntervals };
};

export const getNfbTherapistsForRange = (
    therapistSchedules: Array<NfbTherapistSchedule>,
    timeOffIntervals: Array<TimeOffInterval>,
    rangeStart: Date,
    rangeEnd: Date
) => {
    const userIdsOnTimeOff = new Set(
        timeOffIntervals
            .filter((interval) => interval.start.getTime() < rangeEnd.getTime() && interval.end.getTime() > rangeStart.getTime())
            .map((interval) => interval.userId)
    );

    const therapistsByUserId = new Map<string, NfbTherapistSchedule>();
    therapistSchedules
        .filter(
            (schedule) =>
                schedule.start.getTime() < rangeEnd.getTime() &&
                schedule.end.getTime() > rangeStart.getTime() &&
                !userIdsOnTimeOff.has(schedule.userId)
        )
        .forEach((schedule) => therapistsByUserId.set(schedule.userId, schedule));

    const therapists = Array.from(therapistsByUserId.values());
    return therapists;
};
